import { useEffect, useRef, useState, useCallback } from 'react';
import type { Socket } from 'socket.io-client';
import { apiFetch } from '@/services/apiClient';
import { requestMicrophoneStream } from '@/services/sensorPermissionService';

interface UseWakeWordOptions {
  /** Socket.IO connection for server-side Qwen ASR wake word detection */
  socket?: Socket | null;
  /** Porcupine access key (free at https://picovoice.ai) — optional, server-side Qwen ASR is the default */
  accessKey?: string;
  /** Keyword to detect. Default 'Jarvis' */
  keyword?: string;
  /** Ref to the startCall function from useVoiceCall */
  startCallRef: React.MutableRefObject<((voiceId?: string, personalityId?: string, agentId?: string, options?: { transcriptionOnly?: boolean; domain?: 'personal' | 'work'; orgId?: string }) => Promise<void>)>;
  /** Enable/disable wake word */
  enabled?: boolean;
  /** Sensitivity 0-1. Default 0.5 (Picovoice only) */
  sensitivity?: number;
  /** Voice ID to pass to startCall */
  voiceId?: string;
  /** Personality ID to pass to startCall */
  personalityId?: string;
  /** Agent ID to pass to startCall */
  agentId?: string;
  /** Domain scope to pass to the voice call when wake word starts it */
  startCallOptions?: { domain?: 'personal' | 'work'; orgId?: string };
  /** Called when wake word is detected (before startCall) */
  onDetection?: () => void;
  /** Return false to ignore wake audio/detections, e.g. when voiceprint says this is not the owner. */
  canAcceptWake?: () => boolean;
  canSendWakeAudio?: () => boolean;
  /** If provided and returns true, skip starting a new call (e.g. already in a call) */
  isCallActive?: () => boolean;
  /** Called to interrupt an active call when wake word fires during one */
  onInterrupt?: () => void;
}

interface UseWakeWordReturn {
  isListening: boolean;
  isSupported: boolean;
  lastDetection: string | null;
  error: string | null;
  enable: () => Promise<void>;
  disable: () => void;
}

const PICOVOICE_ACCESS_KEY_STORAGE = 'xf_picovoice_key';

/** Uint8Array → base64 with 4KB chunking (avoids btoa stack overflow on large buffers) */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  const CHUNK = 4096;
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += CHUNK) {
    parts.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
  }
  return btoa(parts.join(''));
}
const WAKE_SERVICE_MISSING_MESSAGE = '语音唤醒需要先在设置 > 语音服务配置豆包语音或 DashScope。';
const WAKE_MIC_UNAVAILABLE_MESSAGE = '当前环境无法使用麦克风：浏览器仅在安全上下文（HTTPS 或本机 localhost）下允许访问麦克风。请改用 https://os.aizjyun.cn 访问本页面以启用语音唤醒。';

async function hasServerWakeProvider(): Promise<boolean | null> {
  try {
    const response = await apiFetch('/api/settings/keys');
    const status = await response.json().catch(() => ({}));
    if (!response.ok) return null;
    return Boolean(status.DOUBAO_SPEECH_KEY || status.DASHSCOPE_API_KEY || status.QWEN_API_KEY);
  } catch (err) {
    console.warn('[WakeWord] 检查服务端唤醒提供者失败，已忽略:', err);
    return null;
  }
}

export function useWakeWord({
  socket,
  accessKey: propKey,
  keyword = 'Jarvis',
  startCallRef,
  enabled = false,
  sensitivity = 0.5,
  voiceId,
  personalityId,
  agentId,
  startCallOptions,
  onDetection,
  canAcceptWake,
  canSendWakeAudio,
  isCallActive,
  onInterrupt,
}: UseWakeWordOptions): UseWakeWordReturn {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [lastDetection, setLastDetection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  // 🔴 v3.12.56: 连续错误计数（防偷跑阈值从1次失败→3次）
  const consecutiveErrorsRef = useRef(0);
  const enabledRef = useRef(enabled);
  const socketRef = useRef(socket);
  const wakeConfigUnavailableRef = useRef(false);
  const wakeHandlersRef = useRef<{
    detected?: (data: { keyword: string; timestamp: string }) => void;
    started?: () => void;
    error?: (data: { message: string }) => void;
  }>({});

  enabledRef.current = enabled;
  socketRef.current = socket;

  // Stable refs for enable/disable to prevent useEffect restart loops (Bug 2 fix)
  const enableRef = useRef<() => Promise<void>>(async () => {});
  const disableRef = useRef<() => void>(() => {});

  const accessKey = propKey || localStorage.getItem(PICOVOICE_ACCESS_KEY_STORAGE) || '';

  const removeWakeHandlers = useCallback(() => {
    const s = socketRef.current;
    const handlers = wakeHandlersRef.current;
    if (s) {
      if (handlers.detected) s.off('wake:detected', handlers.detected);
      if (handlers.started) s.off('wake:started', handlers.started);
      if (handlers.error) s.off('wake:error', handlers.error);
    }
    wakeHandlersRef.current = {};
  }, []);

  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch (e) { console.warn('[useWakeWord] 捕获到异常:', e); }
      processorRef.current = null;
    }
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const disable = useCallback(() => {
    setIsListening(false);
    const s = socketRef.current;
    if (s?.connected) {
      s.emit('wake:stop');
    }
    removeWakeHandlers();
    cleanupAudio();
  }, [cleanupAudio, removeWakeHandlers]);

  // ── Server-side Qwen ASR wake detection (primary) ──

  const enableQwenWake = useCallback(async () => {
    const s = socketRef.current;
    if (!s?.connected) {
      setError('Socket not connected — retrying...');
      return;
    }

    if (wakeConfigUnavailableRef.current) {
      setIsListening(false);
      setIsSupported(false);
      setError(WAKE_SERVICE_MISSING_MESSAGE);
      return;
    }

    // 浏览器仅在安全上下文（HTTPS / localhost）下暴露 getUserMedia。
    // 非安全上下文（如 http:// 访问）下直接提示，避免抛原生英文错误。
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setIsListening(false);
      setIsSupported(false);
      setError(WAKE_MIC_UNAVAILABLE_MESSAGE);
      return;
    }

    const hasProvider = await hasServerWakeProvider();
    if (hasProvider === false) {
      wakeConfigUnavailableRef.current = true;
      setIsListening(false);
      setIsSupported(false);
      setError(WAKE_SERVICE_MISSING_MESSAGE);
      return;
    }

    try {
      setError(null);
      console.log('[WakeWord-Qwen] Opening microphone...');

      const stream = await requestMicrophoneStream({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      streamRef.current = stream;
      console.log('[WakeWord-Qwen] Mic opened, setting up AudioContext');

      const ctx = new AudioContext({ sampleRate: 16000 });
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (!enabledRef.current) return;
        if (canSendWakeAudio && !canSendWakeAudio()) return;
        try {
          const input = event.inputBuffer.getChannelData(0);
          const pcm = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            pcm[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
          }
          const rawBytes = new Uint8Array(pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength));
        const b64 = uint8ArrayToBase64(rawBytes);
          socketRef.current?.emit('wake:audio', { audio: b64, encoding: 'base64' });
        } catch { /* ignore */ }
      };

      source.connect(processor);
      processor.connect(ctx.destination);

      // Set up listeners BEFORE emitting wake:start
      removeWakeHandlers();

      const onDetected = (data: { keyword: string; timestamp: string }) => {
        if (canAcceptWake && !canAcceptWake()) {
          console.log('[WakeWord-Qwen] Ignored detection: voice gate closed');
          return;
        }
        console.log('[WakeWord-Qwen] Detected:', data.keyword);
        setLastDetection(data.timestamp);
        onDetection?.();

        if (isCallActive?.()) {
          onInterrupt?.();
        } else {
          startCallRef.current?.(voiceId, personalityId, agentId, startCallOptions);
        }
      };

      const onStarted = () => {
        console.log('[WakeWord-Qwen] Server confirmed, listening');
        setIsListening(true);
        setIsSupported(true);
      };

      const onError = (data: { message: string }) => {
        console.warn('[WakeWord-Qwen] Server error:', data.message);
        const message = data.message || '';
        if (/required for wake word detection|not configured|no DashScope key/i.test(message)) {
          wakeConfigUnavailableRef.current = true;
          setIsListening(false);
          setIsSupported(false);
          setError(WAKE_SERVICE_MISSING_MESSAGE);
          cleanupAudio();
          try { s.emit('wake:stop'); } catch (e) { console.warn('[useWakeWord] 捕获到异常:', e); }
          removeWakeHandlers();
          return;
        }
        // 🔴 v3.12.55: Auto-disable wake on fatal ASR errors to prevent sneaky DashScope billing
        // v3.12.56: 阈值改为连续 3 次失败（避免网络抖动误关闭）
        if (/语音唤醒连接已断开|语音唤醒连接失败|WebSocket|wake|error/i.test(message)) {
          consecutiveErrorsRef.current += 1;
          console.warn(`[WakeWord-Qwen] Wake error count: ${consecutiveErrorsRef.current}/3`, message);
          if (consecutiveErrorsRef.current >= 3) {
            console.warn('[WakeWord-Qwen] 3 consecutive errors, auto-disabling to prevent billing');
            consecutiveErrorsRef.current = 0;
            setIsListening(false);
            setIsSupported(false);
            setError(message || '语音唤醒连续失败，已自动关闭以节省费用。请检查 DashScope 配置后重试。');
            cleanupAudio();
            try { s.emit('wake:stop'); } catch (e) { console.warn('[useWakeWord] 捕获到异常:', e); }
            removeWakeHandlers();
            try { localStorage.setItem('xf_wake_word_enabled', 'false'); } catch {}
            return;
          }
          // 1-2 次失败：只显示错误，不关闭
          setError(`${message} (重试 ${consecutiveErrorsRef.current}/3 后将自动关闭)`);
          return;
        }
        // 非致命错误，重置计数
        consecutiveErrorsRef.current = 0;
        setError(data.message);
      };

      wakeHandlersRef.current = { detected: onDetected, started: onStarted, error: onError };
      s.on('wake:detected', onDetected);
      s.on('wake:started', onStarted);
      s.on('wake:error', onError);

      console.log('[WakeWord-Qwen] Emitting wake:start');
      s.emit('wake:start');
    } catch (err: any) {
      cleanupAudio();
      const msg = err.message || '唤醒词启动失败';
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setError('麦克风权限被拒绝，请在浏览器中允许麦克风访问后重试。');
      } else if (/required for wake word detection|not configured|no DashScope key/i.test(msg)) {
        wakeConfigUnavailableRef.current = true;
        setIsListening(false);
        setIsSupported(false);
        setError(WAKE_SERVICE_MISSING_MESSAGE);
      } else if (/unavailable in this runtime|getUserMedia/i.test(msg)) {
        setIsListening(false);
        setIsSupported(false);
        setError(WAKE_MIC_UNAVAILABLE_MESSAGE);
      } else {
        setError(msg);
      }
    }
  }, [voiceId, personalityId, agentId, startCallOptions, startCallRef, cleanupAudio, removeWakeHandlers, onDetection, canAcceptWake, canSendWakeAudio, isCallActive, onInterrupt]);

  // ── Picovoice on-device detection (fallback) ──

  const enablePicovoice = useCallback(async () => {
    try {
      setError(null);

      const { Porcupine, BuiltInKeyword } = await import('@picovoice/porcupine-web');

      const keywordMap: Record<string, typeof BuiltInKeyword[keyof typeof BuiltInKeyword]> = {
        'Porcupine': BuiltInKeyword.Porcupine,
        'Computer': BuiltInKeyword.Computer,
        'Hey Google': BuiltInKeyword.HeyGoogle,
        'Alexa': BuiltInKeyword.Alexa,
        'Jarvis': BuiltInKeyword.Jarvis,
      };

      const detectionCallback = (_detection: any) => {
        if (canAcceptWake && !canAcceptWake()) return;
        setLastDetection(new Date().toISOString());
        onDetection?.();
        if (isCallActive?.()) {
          onInterrupt?.();
          return;
        }
        startCallRef.current?.(voiceId, personalityId, agentId, startCallOptions);
      };

      let engine: any;
      const builtinKeyword = keywordMap[keyword];

      if (builtinKeyword) {
        engine = await Porcupine.create(
          accessKey,
          { builtin: builtinKeyword, sensitivity },
          detectionCallback,
          { publicPath: '/porcupine_params.pv' },
        );
      } else {
        const safeName = keyword.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const customPath = `/porcupine/${safeName}.ppn`;
        try {
          engine = await Porcupine.create(
            accessKey,
            { publicPath: customPath, label: keyword, sensitivity },
            detectionCallback,
            { publicPath: '/porcupine_params.pv' },
          );
        } catch {
          console.warn(`[WakeWord] Custom keyword "${keyword}" not found, falling back to "Jarvis"`);
          engine = await Porcupine.create(
            accessKey,
            { builtin: BuiltInKeyword.Jarvis, sensitivity },
            detectionCallback,
            { publicPath: '/porcupine_params.pv' },
          );
        }
      }

      const stream = await requestMicrophoneStream({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      streamRef.current = stream;

      const ctx = new AudioContext({ sampleRate: engine.sampleRate });
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(engine.frameLength, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (!enabledRef.current) return;
        try {
          const input = event.inputBuffer.getChannelData(0);
          const pcm = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            pcm[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
          }
          engine.process(pcm);
        } catch { /* ignore */ }
      };

      source.connect(processor);
      processor.connect(ctx.destination);
      setIsListening(true);
      setIsSupported(true);
    } catch (err: any) {
      cleanupAudio();
      const msg = err.message || '唤醒词初始化失败';
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setError('Microphone permission denied.');
      } else if (msg.includes('Porcupine') || msg.includes('Pv')) {
        setError(msg);
      } else {
        setError(msg);
      }
    }
  }, [accessKey, keyword, sensitivity, voiceId, personalityId, agentId, startCallOptions, startCallRef, cleanupAudio, canAcceptWake, isCallActive, onDetection, onInterrupt]);

  const enable = useCallback(async () => {
    // Stop any existing session first
    disable();

    if (accessKey) {
      console.log('[WakeWord] Using Picovoice (on-device)');
      await enablePicovoice();
    } else if (socketRef.current?.connected) {
      console.log('[WakeWord] Using Qwen ASR (server-side)');
      await enableQwenWake();
    } else {
      console.log('[WakeWord] No Picovoice key and socket not connected, waiting...');
      setError('Waiting for connection...');
    }
  }, [accessKey, disable, enablePicovoice, enableQwenWake]);

  // Update stable refs after enable/disable are defined (TDZ fix)
  enableRef.current = enable;
  disableRef.current = disable;

  // Listen for socket disconnect/reconnect — reset so wake auto-restarts on reconnect
  useEffect(() => {
    const s = socketRef.current;
    if (!s) return;

    const onDisconnect = () => {
      console.log('[WakeWord] Socket disconnected, resetting...');
      setIsListening(false);
      setError('Connection lost — reconnecting...');
    };
    const onReconnect = () => {
      console.log('[WakeWord] Socket reconnected');
      setError(null);
    };

    s.on('disconnect', onDisconnect);
    // socket.io-client fires 'connect' on initial AND reconnect
    const onConnect = () => {
      if (!s.connected) return;
      onReconnect();
    };
    s.on('connect', onConnect);

    return () => {
      s.off('disconnect', onDisconnect);
      s.off('connect', onConnect);
    };
  }, [socket?.id]);

  useEffect(() => {
    const onKeysChanged = () => {
      wakeConfigUnavailableRef.current = false;
      if (enabledRef.current) {
        setError(null);
        setTimeout(() => { void enable(); }, 0);
      }
    };
    window.addEventListener('xf:keys-changed', onKeysChanged);
    return () => window.removeEventListener('xf:keys-changed', onKeysChanged);
  }, [enable]);

  // Auto-start / stop — includes socket state so it retries when connection becomes available
  useEffect(() => {
    console.log('[WakeWord] State change — enabled:', enabled, 'isListening:', isListening, 'socket:', !!socketRef.current?.connected);
    if (enabled && !isListening && !wakeConfigUnavailableRef.current) {
      enableRef.current();
    } else if (!enabled && isListening) {
      disableRef.current();
    }
  }, [enabled, isListening, socket?.connected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { disable(); };
  }, []);

  return { isListening, isSupported, lastDetection, error, enable, disable };
}

export function savePicovoiceKey(key: string) {
  try { localStorage.setItem(PICOVOICE_ACCESS_KEY_STORAGE, key); } catch {}
}

export function getPicovoiceKey(): string | null {
  try { return localStorage.getItem(PICOVOICE_ACCESS_KEY_STORAGE); } catch { return null; }
}
